/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {batch} from 'react-redux';
import {error} from '@ion/core/src/logger/logger';
import {
  authenticate,
  createSubscriptionPlanUrl,
  createUser,
  deleteUser,
  getAsset,
  getAssetCurrentState,
  getAssets,
  getSettings,
  getSubscriptionPlans,
  getSubscriptions,
  getUser,
  getUsers,
  updateAsset,
  updateUser,
  updateUserPassword
} from '@ion/core/src/services/SDConnect';
import {
  Asset,
  CVSAssetType,
  Entity,
  GenericObject,
  User
} from '@ion/core/src/types/entities';
import {GenericFunction} from '@ion/core/src/types/general';
import {BasemapTypes} from '@ion/features/src/maps/types/types';
import {getStatus, isWarningStatus} from '@/components/sidesheet/SubscriptionDetails';
import changePage, {Route} from '@/routes/RouteDefinitions';
import {setSession} from '@/security/Session';
import {PageInitializeAction} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {parseStringToObject} from '../../utils/ComponentUtils';
import {initializeAssetObservers} from './initialization';
import {persistUser, UPDATE_CURRENT_USER} from './login';
import {toggleUserPrompt} from './user-prompt';

export const INITIALIZE_DASHBOARD      = 'INITIALIZE_DASHBOARD';
export const SET_PAYMENT_URL           = 'SET_PAYMENT_URL';
export const SET_MAP_INITIALIZED       = 'SET_MAP_INITIALIZED';
export const SET_MAP_BASEMAP           = 'SET_MAP_BASEMAP';
export const SET_MAP_EXTENT            = 'SET_MAP_EXTENT';
export const SET_MAP_ZOOM              = 'SET_MAP_ZOOM';
export const SET_ASSETS_LOADED         = 'SET_ASSETS_LOADED';
export const SET_ASSETS_ERROR          = 'SET_ASSETS_ERROR';
export const SET_CURRENT_STATE_ERROR   = 'SET_CURRENT_STATE_ERROR';
export const SET_ASSETS                = 'SET_ASSETS';
export const CLEAR_ASSETS              = 'CLEAR_ASSETS';
export const SET_SELECTED_ASSET        = 'SET_SELECTED_ASSET';
export const CLEAR_SELECTED_ASSET      = 'CLEAR_SELECTED_ASSET';
export const ASSET_UPDATED             = 'ASSET_UPDATED';
export const ASSET_DIALOG_SHOW         = 'ASSET_DIALOG_SHOW';
export const EMERGENCY_DIALOG_SHOW     = 'EMERGENCY_DIALOG_SHOW';
export const SUBSCRIPTION_DIALOG_SHOW  = 'SUBSCRIPTION_DIALOG_SHOW';
export const USER_PROFILE_DIALOG_SHOW  = 'USER_PROFILE_DIALOG_SHOW';
export const EDIT_ADMIN_ASSET_REQUEST  = 'EDIT_ADMIN_ASSET_REQUEST';
export const EDIT_ADMIN_ASSET_RESPONSE = 'EDIT_ADMIN_ASSET_RESPONSE';
export const EDIT_ADMIN_ASSET_ERROR    = 'EDIT_ADMIN_ASSET_ERROR';
export const EDIT_USER_REQUEST         = 'EDIT_USER_REQUEST';
export const EDIT_USER_RESPONSE        = 'EDIT_USER_RESPONSE';
export const EDIT_USER_ERROR           = 'EDIT_USER_ERROR';
export const SET_SUBSCRIPTION_PLANS    = 'SET_SUBSCRIPTION_PLANS';
export const SET_USERS_LOADED          = 'SET_USERS_LOADED';
export const SET_USERS                 = 'SET_USERS';
export const CLEAR_USERS               = 'CLEAR_USERS';
export const ADD_USER_REQUEST          = 'ADD_USER_REQUEST';
export const ADD_USER_RESPONSE         = 'ADD_USER_RESPONSE';
export const ADD_USER_ERROR            = 'ADD_USER_ERROR';
export const SET_SETTINGS              = 'SET_SETTINGS';
export const DELETE_USER_REQUEST       = 'DELETE_USER_REQUEST';
export const DELETE_USER_RESPONSE      = 'DELETE_USER_RESPONSE';
export const DELETE_USER_ERROR         = 'DELETE_USER_ERROR';

export const initializeDashboard = (): PageInitializeAction => ({
  type : INITIALIZE_DASHBOARD
});

export const setMapInitialized = () => ({
  type : SET_MAP_INITIALIZED
});

export const setMapBasemap = (basemap: BasemapTypes) => ({
  type : SET_MAP_BASEMAP, basemap
});

export const setMapExtent = (extent: any) => ({
  type : SET_MAP_EXTENT, extent
});

export const setMapZoom = (zoom: number) => ({
  type : SET_MAP_ZOOM, zoom
});

const getAugmentedAsset = (asset: Asset, precedenceData: GenericObject, subscriptionData: GenericObject) => {
  const subscription = subscriptionData.filter(({vin}) => vin === asset.vin)?.[0] || {};
  const status = getStatus(subscription?.currentPlan);
  if (precedenceData && isWarningStatus(status)) {
    delete precedenceData.latitude;
    delete precedenceData.longitude;
  }
  return {
    ...asset,
    ...precedenceData,
    ...precedenceData.address,
    subscriptionData : subscription
  };
};

const updateAssets = (assetId: number, augmented: CVSAssetType, assets: CVSAssetType[]) => {
  const tempAssets = [...assets];
  let updatedAssets;
  const matchingIndex = tempAssets.findIndex(asset => asset.id === assetId);
  if (matchingIndex > -1) {
    tempAssets[matchingIndex] = {...augmented};
    updatedAssets = tempAssets;
  }

  return updatedAssets;
};

const getPrecedenceData = ({precedenceData}: GenericObject = {}) => {
  const assetData = {};
  if (precedenceData) {
    const {address, latitude, longitude} = precedenceData;
    if (address) {
      assetData.address = {...parseStringToObject(address.value)};
    }
    if (latitude) {
      assetData.latitude = parseFloat(latitude.value);
    }
    if (longitude) {
      assetData.longitude = parseFloat(longitude.value);
    }
  }
  return assetData;
};

const verifyAuthentication = async(getState, updated, user) => {
  if (user.passwordChange) {
    const {username} = updated;
    const password   = user.passwordChange.newPassword;
    if (username && password) {
      const persist = getState().login.persistCredentials;
      const user = await authenticate(username, password);
      persistUser(persist, username, password);
      if (!user) {
        changePage(Route.LOGIN);
        return false;
      }
    }
    setSession();
  }
};

export const loadAssets = () => {
  return async(dispatch) => {
    dispatch(setAssetsLoaded(false));
    try {
      const assets = await getAssets();
      const augmented = [];
      if (assets.length > 0) {
        const subscriptionData = await getSubscriptions();
        await Promise.all(assets?.map(async(asset: CVSAssetType) => {
          const currentState   = await getAssetCurrentState(asset.id);
          const precedenceData = getPrecedenceData(currentState?.assetState);
          augmented.push(getAugmentedAsset(asset, precedenceData, subscriptionData));
        }));
        if (augmented?.length === 1) {
          dispatch(setSelectedAsset(augmented[0]));
        }
      }
      batch(() => {
        dispatch(setAssets(augmented));
        dispatch(setAssetsError(false));
      });
      initializeAssetObservers(augmented, dispatch);
    } catch (err) {
      if (err?.is404 && err?.url?.includes('currentState')) {
        dispatch(setCurrentStateError(true));
      }
      error(err);
      batch(() => {
        dispatch(clearSelectedAsset());
        dispatch(clearAssets());
        dispatch(setAssetsError(true));
      });
    }
    dispatch(setAssetsLoaded(true));
  };
};

export const editAsset = (asset: Asset, onlySubscription: boolean = false) => {
  return async(dispatch: Function, getState): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ASSET_REQUEST});
    try {
      let updated = null;
      const {assets}       = getState().dashboard;
      const refreshedAsset = await getAsset(asset.id);
      if (!onlySubscription) {
        const payload = {...refreshedAsset, name: asset.name};
        updated       = await updateAsset(payload);
      }
      const subscriptionData = await getSubscriptions();
      const currentState     = await getAssetCurrentState(asset.id);
      const precedenceData   = getPrecedenceData(currentState?.assetState);
      const augmented        = getAugmentedAsset(updated || refreshedAsset, precedenceData, subscriptionData);
      dispatch({type: EDIT_ADMIN_ASSET_RESPONSE, asset: updated});
      dispatch(setSelectedAsset(augmented));
      if (assets.all.length > 0) {
        const updatedAssets = updateAssets(asset.id, augmented, assets.all);
        dispatch(setAssets(updatedAssets));
      }
    } catch (err) {
      dispatch({type: EDIT_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

export const loadUsers = () => {
  return async(dispatch, getState) => {
    dispatch(setUsersLoaded(false));
    try {
      const allUsers = await getUsers();
      const {user}   = getState().login;
      const users    = allUsers.filter(({id}) => id !== user.id);
      dispatch(setUsers(users));
    } catch (err) {
      error(err);
      dispatch(clearUsers());
    }
    dispatch(setUsersLoaded(true));
  };
};

export const addNewUser = (user: User) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_USER_REQUEST});
    try {
      const updated = await createUser(user);
      dispatch(loadUsers());
      dispatch({type: ADD_USER_RESPONSE, user: updated});
      dispatch(toggleUserPrompt(successAddOptions(Entity.user)));
    } catch (err) {
      dispatch({type: ADD_USER_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.user, err)));
      error(err);
    }
  };
};

export const editUser = (user: User) => {
  return async(dispatch: Function, getState): Promise<any> => {
    dispatch({type: EDIT_USER_REQUEST});
    try {
      const current              = !user.passwordChange && await getUser(user.id);
      const {user: currentUser}  = getState().login;
      const updated              = user.passwordChange
        ? await updateUserPassword(user.id, {passwordChange: user.passwordChange})
        : await updateUser({...current, ...user});
      await verifyAuthentication(getState, updated, user);
      dispatch({type: EDIT_USER_RESPONSE, user: updated});
      if (user.id === currentUser.id) {
        dispatch({type: UPDATE_CURRENT_USER, user: updated});
      }
      dispatch(loadUsers());
      dispatch(toggleUserPrompt(successEditOptions(Entity.user)));
    } catch (err) {
      dispatch({type: EDIT_USER_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.user, err)));
      error(err);
    }
  };
};

export const getCurrentUser = (user) => {
  return async(dispatch: Function): Promise<any> => {
    try {
      const currentUser = await getUser(user.id);
      dispatch({type: UPDATE_CURRENT_USER, user: currentUser});
    } catch (err) {
      error('Unable to retrieve authenticated User Entity', err);
    }
  };
};

export const removeUser = (user: User) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_USER_REQUEST});
    try {
      const deleted = await deleteUser(user);
      dispatch(loadUsers());
      dispatch({type: DELETE_USER_RESPONSE, user: deleted});
    } catch (err) {
      dispatch({type: DELETE_USER_ERROR, error: err});
      error(err);
    }
  };
};

export const setAssetsLoaded = (loaded: boolean) => ({
  type : SET_ASSETS_LOADED, loaded
});

export const setAssetsError = (error: boolean) => ({
  type : SET_ASSETS_ERROR, error
});

export const setCurrentStateError = (error: boolean) => ({
  type : SET_CURRENT_STATE_ERROR, error
});

export const setAssets = (assets: CVSAssetType[]) => ({
  type : SET_ASSETS, assets
});

export const setSelectedAsset = (asset: CVSAssetType) => ({
  type : SET_SELECTED_ASSET, asset
});

/**
 * Dispatched when changed have been received for an observed asset, at which
 * point the reducer will update the asset in the store with the given id to
 * include the latest changes.
 */
export const assetUpdated = (change: GenericObject, id: number) => ({
  type : ASSET_UPDATED, change, id
});

export const clearAssets = () => ({
  type : CLEAR_ASSETS
});

export const clearSelectedAsset = () => ({
  type : CLEAR_SELECTED_ASSET
});

export const showAssetDialog = (show: boolean) => ({
  type : ASSET_DIALOG_SHOW, show
});

export const showEmergencyDialog = (show: boolean) => ({
  type : EMERGENCY_DIALOG_SHOW, show
});

export const showSubscriptionDialog = (show: boolean) => ({
  type : SUBSCRIPTION_DIALOG_SHOW, show
});

export const showUserProfileDialog = (show: boolean) => ({
  type : USER_PROFILE_DIALOG_SHOW, show
});

export const loadSubscriptionPlans = (vin: string) => {
  return async(dispatch) => {
    let plans;
    try {
      plans = await getSubscriptionPlans(vin);
    } catch (err) {
      plans = [];
      error(err);
    }
    dispatch({type: SET_SUBSCRIPTION_PLANS, plans});
  };
};

export const getPaymentUrl = (vin, planId, subscriptionId) => {
  return async dispatch => {
    let url = '';
    try {
      url = await createSubscriptionPlanUrl(vin, planId, subscriptionId);
    } catch (err) {
      error(err);
    }
    dispatch(setPaymentUrl(url));
  };
};

export const setPaymentUrl = (url: string) => ({
  type : SET_PAYMENT_URL, url
});

export const setUsersLoaded = (loaded: boolean) => ({
  type : SET_USERS_LOADED, loaded
});

export const setUsers = (users: User[]) => ({
  type : SET_USERS, users
});

export const clearUsers = () => ({
  type : CLEAR_USERS
});

export const loadSettings = () => {
  return async(dispatch) => {
    let settings;
    try {
      settings = await getSettings();
    } catch (err) {
      error(err);
    }
    dispatch({type: SET_SETTINGS, settings});
  };
};
